import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import Label from "src/components/Label";
import { getTrackersApi } from "src/DAL/tracker/tracker";
import { normalizeTracker } from "./trackerStorage";
import { clearLeftReviews } from "./trackerDashboardStats";
import {
  getInitials,
  getSubmissionReviewLabel,
  isScheduledReviewRow,
  loadSubmissionsFromApi,
  SUBMISSION_REVIEW,
} from "./submissionStorage";

const PRIMARY = "var(--portal-theme-primary)";
const BORDER = "#e2e8f0";
const MUTED = "#637381";
const TEXT = "#212B36";

const reviewColor = (status) => {
  if (status === SUBMISSION_REVIEW.OVERDUE) return "error";
  if (status === SUBMISSION_REVIEW.PENDING) return "warning";
  if (status === SUBMISSION_REVIEW.REVIEWED) return "success";
  return "default";
};

/**
 * Scheduled full-journey coach reviews (not check-in chat/email).
 * Same TrackerSubmission model — filtered by reviewDetail schedule labels.
 */
export default function TrackerReviewsList({
  /** When set, only rows for this coach (consultant portal). */
  coachIdFilter = null,
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);

  const trackerFilter = searchParams.get("tracker") || "all";
  const statusFilter = searchParams.get("status") || "pending";

  const load = async () => {
    setLoading(true);
    try {
      const trackerId = trackerFilter !== "all" ? trackerFilter : null;
      const [subsResult, trackersRes] = await Promise.all([
        loadSubmissionsFromApi(trackerId, { limit: 500 }),
        getTrackersApi({ limit: 200 }),
      ]);
      setRows(clearLeftReviews(subsResult.rows || []));
      if (trackersRes?.code === 200 && Array.isArray(trackersRes.trackers)) {
        setTrackers(
          trackersRes.trackers.map(normalizeTracker).filter(Boolean)
        );
      } else {
        setTrackers([]);
      }
      if (!subsResult.ok) {
        enqueueSnackbar(subsResult.message || "Failed to load reviews", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackerFilter]);

  const scheduledRows = useMemo(() => {
    let list = (rows || []).filter(isScheduledReviewRow);
    if (coachIdFilter) {
      const me = String(coachIdFilter);
      list = list.filter(
        (r) =>
          String(r.coachId || "") === me ||
          // Fallback: tracker assigns this coach
          trackers.some(
            (t) =>
              String(t.id) === String(r.trackerId) &&
              (t.coaches || []).some((c) => String(c.id) === me)
          )
      );
    }
    if (statusFilter === "pending") {
      list = list.filter(
        (r) =>
          r.reviewStatus === SUBMISSION_REVIEW.PENDING ||
          r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
      );
    } else if (statusFilter === "overdue") {
      list = list.filter((r) => r.reviewStatus === SUBMISSION_REVIEW.OVERDUE);
    } else if (statusFilter === "reviewed") {
      list = list.filter((r) => r.reviewStatus === SUBMISSION_REVIEW.REVIEWED);
    }
    return [...list].sort((a, b) => {
      const rank = (s) =>
        s === SUBMISSION_REVIEW.OVERDUE
          ? 0
          : s === SUBMISSION_REVIEW.PENDING
            ? 1
            : 2;
      const d = rank(a.reviewStatus) - rank(b.reviewStatus);
      if (d !== 0) return d;
      return (
        new Date(b.updatedAt || b.submittedAt || 0) -
        new Date(a.updatedAt || a.submittedAt || 0)
      );
    });
  }, [rows, trackers, coachIdFilter, statusFilter]);

  const stats = useMemo(() => {
    const base = (rows || []).filter(isScheduledReviewRow).filter((r) => {
      if (!coachIdFilter) return true;
      const me = String(coachIdFilter);
      return (
        String(r.coachId || "") === me ||
        trackers.some(
          (t) =>
            String(t.id) === String(r.trackerId) &&
            (t.coaches || []).some((c) => String(c.id) === me)
        )
      );
    });
    return {
      due: base.filter(
        (r) =>
          r.reviewStatus === SUBMISSION_REVIEW.PENDING ||
          r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
      ).length,
      overdue: base.filter((r) => r.reviewStatus === SUBMISSION_REVIEW.OVERDUE)
        .length,
      done: base.filter((r) => r.reviewStatus === SUBMISSION_REVIEW.REVIEWED)
        .length,
    };
  }, [rows, trackers, coachIdFilter]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const MENU_OPTIONS = [
    {
      label: "Open review",
      icon: "mdi:clipboard-check-outline",
      handleClick: (row) => navigate(`/tracker-reviews/${row.id}`),
    },
    {
      label: "Open submission",
      icon: "mdi:inbox-outline",
      handleClick: (row) => navigate(`/tracker-submissions/${row.id}`),
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "person",
      label: "Person",
      renderData: (row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
          }}
          onClick={() => navigate(`/tracker-reviews/${row.id}`)}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 12,
              fontWeight: 700,
              bgcolor: PRIMARY,
            }}
          >
            {getInitials(row.memberName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TEXT }}>
              {row.memberName || "—"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              {row.email || "—"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "milestone",
      label: "Scheduled review",
      renderData: (row) => (
        <Box
          sx={{ cursor: "pointer" }}
          onClick={() => navigate(`/tracker-reviews/${row.id}`)}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TEXT }}>
            {row.reviewDetail || "Scheduled review"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            {row.trackerName || "Journey"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "coach",
      label: "Coach",
      renderData: (row) => (
        <Typography sx={{ fontSize: 13 }}>{row.coachName || "—"}</Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      renderData: (row) => (
        <Label variant="ghost" color={reviewColor(row.reviewStatus)}>
          {getSubmissionReviewLabel(row.reviewStatus)}
        </Label>
      ),
    },
    {
      id: "updated",
      label: "Updated",
      renderData: (row) => (
        <Typography sx={{ fontSize: 13 }}>
          {row.updatedAt || row.submittedAt
            ? moment(row.updatedAt || row.submittedAt).format("DD MMM YYYY")
            : "—"}
        </Typography>
      ),
    },
  ];

  const tableRows = scheduledRows.map((row) => ({
    ...row,
    MENU_OPTIONS,
  }));

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <div className="container-fluid tracker-module-page">
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}
        >
          Scheduled reviews
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.35, color: "text.secondary", fontSize: 13 }}
        >
          Full journey reviews from journey schedule settings (week / day /
          fixed dates). Check-in chat &amp; email feedback stays on Submissions.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 2 }}>
        {[
          {
            key: "pending",
            label: "Due",
            value: stats.due,
            tone: "#b45309",
          },
          {
            key: "overdue",
            label: "Overdue",
            value: stats.overdue,
            tone: stats.overdue ? "#b91c1c" : MUTED,
          },
          {
            key: "reviewed",
            label: "Completed",
            value: stats.done,
            tone: "#047857",
          },
          { key: "all", label: "All", value: stats.due + stats.done, tone: PRIMARY },
        ].map((card) => (
          <Box
            key={card.key}
            component="button"
            type="button"
            onClick={() => setParam("status", card.key)}
            sx={{
              flex: "1 1 120px",
              minWidth: 110,
              p: 1.5,
              borderRadius: "12px",
              border: `1.5px solid ${
                statusFilter === card.key ? card.tone : BORDER
              }`,
              bgcolor: "#fff",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
              {card.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 22,
                fontWeight: 600,
                color: card.tone,
              }}
            >
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <TextField
          select
          size="small"
          label="Journey"
          value={trackerFilter}
          onChange={(e) => setParam("tracker", e.target.value)}
          sx={{ minWidth: 220, bgcolor: "#fff" }}
        >
          <MenuItem value="all">All journeys</MenuItem>
          {trackers.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name || "Untitled"}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <CustomMUITable
        TABLE_HEAD={TABLE_HEAD}
        data={tableRows}
        MENU_OPTIONS={MENU_OPTIONS}
        hide_search
        className="card-with-background tracker-module-table"
      />

      {!scheduledRows.length ? (
        <Typography
          sx={{ mt: 2, textAlign: "center", color: MUTED, fontSize: 13.5 }}
        >
          No scheduled reviews in this filter. They appear when a journey’s
          review schedule day is reached.
        </Typography>
      ) : null}
    </div>
  );
}
